'use client';

import { usePublicContent } from '@/lib/use-public-content';
import { cn } from '@/lib/utils';
import type { Partner } from '@/types/content';
import { PathEnum } from '@/types/enums';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Button from '../ui/Button';
import Link from '../ui/Link';

export default function PartnersSectionClient({
    initialPartners,
    showAllPartners = false,
}: {
    initialPartners: Partner[];
    showAllPartners?: boolean;
}) {
    const partners = usePublicContent('partners', initialPartners);
    const t = useTranslations();

    const partnersToDisplay = showAllPartners
        ? partners
        : partners.filter((partner) => partner.showOnHomepage);

    const linkClasses = cn(
        'relative w-1/4 max-lg:w-full h-[360px] lg:h-[260px] xl:h-[300px] 2xl:h-[390px] group flex justify-center items-center border border-blue-grey bg-light-grey overflow-hidden p-4',
    );

    const imageClasses = cn(
        'relative z-10 lg:opacity-80 group-hover:opacity-100 transition-all duration-700 object-contain w-fit max-xl:max-w-[190px]',
        showAllPartners && 'lg:opacity-100',
    );

    const titleClasses = cn(
        'text-base font-normal absolute bottom-4 text-center text-dark-grey z-10 px-4 line-clamp-2',
        !showAllPartners && 'transition-colors duration-700 lg:group-hover:text-white',
    );

    const coverClasses = cn(
        'h-full w-full bg-light-grey absolute top-0 right-0 transition-all duration-700 lg:group-hover:translate-x-full ease-in-out',
        showAllPartners && 'lg:group-hover:translate-x-0',
    );

    const partnersSectionClasses = cn(
        'lg:container mx-auto flex flex-wrap w-full max-lg:flex-col mt-[60px] max-lg:mt-7 max-lg:px-4 lg:px-0',
        showAllPartners && 'mt-20 max-lg:mt-20',
    );

    return (
        <section className="w-full bg-dark-grey flex flex-col justify-center lg:items-center py-[120px] max-lg:py-20">
            <div className="w-full container mx-auto flex justify-between max-lg:flex-col gap-4 max-lg:px-4 lg:px-0">
                {showAllPartners ? (
                    <div className="flex flex-col gap-[60px] w-full">
                        <h3 className="text-white max-lg:text-h3 text-h1 leading-tight">
                            {t('OurPartnersPage.weAreTrustedPartners')}
                        </h3>
                        <div className="flex justify-between items-center gap-10 w-full max-lg:flex-col max-lg:items-start">
                            <p className="text-white lg:w-1/2 w-full">
                                {t('OurPartnersPage.ourNetworkOfPartners')}
                            </p>
                            <Link
                                className="get-offer-trigger"
                                href={PathEnum.Contact}
                                ariaLabel={t('Buttons.getOffer')}
                            >
                                <>
                                    <span className="sr-only">{t('Buttons.getOffer')}</span>
                                    <Button
                                        title={t('Buttons.getOffer')}
                                        withArrow
                                        variant="secondary"
                                        className="text-white w-max border-white"
                                    />
                                </>
                            </Link>
                        </div>
                    </div>
                ) : (
                    <h3 className="text-white w-full text-center max-lg:text-h3 text-h1">
                        {t('HomePage.weAreTrusted')}
                    </h3>
                )}
            </div>
            <div className={partnersSectionClasses}>
                {partnersToDisplay.map(({ logo, link, name, hoverImage }, index) => (
                    <Link className={linkClasses} href={link} target="_blank" key={index}>
                        <>
                            <Image
                                className={imageClasses}
                                width={270}
                                height={84}
                                src={logo}
                                alt={name}
                            />
                            {!showAllPartners && hoverImage ? (
                                <Image
                                    className="object-cover"
                                    fill
                                    src={hoverImage}
                                    alt=""
                                />
                            ) : null}
                            <span className={titleClasses}>{name}</span>
                            <div className={coverClasses} />
                        </>
                    </Link>
                ))}
                {showAllPartners && (
                    <Link
                        className="w-1/4 max-lg:w-full flex justify-center items-center max-lg:mt-12 get-offer-trigger"
                        href={PathEnum.Contact}
                    >
                        <Button
                            className="text-white border-white w-max"
                            title={t('Buttons.getOffer')}
                            withArrow
                            variant="secondary"
                        />
                    </Link>
                )}
            </div>
        </section>
    );
}
